"use client";

import { ChevronLeft } from "lucide-react";
import { useRef } from "react";
import {
  CommentThread,
  type CommentThreadProps,
  type CommentThreadViewer,
  useCommentFocus,
} from "@/components/posts/comment-thread.client";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useDrawerViewport } from "@/hooks/use-drawer-viewport";

export type CommentsSheetViewer = CommentThreadViewer;

export type CommentsSheetProps = CommentThreadProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const COMMENT_DRAWER_LAYER = 10_000;
const COMMENT_NESTED_DRAWER_LAYER = COMMENT_DRAWER_LAYER + 20;

/**
 * Drawer-presented comment thread. The thread body (list, replies, emoji
 * shortcuts, composer, likes sheet) lives in `CommentThread`, which inline
 * surfaces (e.g. the event Discussion tab) mount directly.
 */
export function CommentsSheet(props: CommentsSheetProps) {
  const { open, onOpenChange, ...threadProps } = props;
  const focus = useCommentFocus();
  const drawerRef = useRef<HTMLDivElement>(null);
  const viewportStyle = useDrawerViewport(open, drawerRef);
  const derivedCanModerate =
    threadProps.canModerate ??
    (threadProps.entityType !== "activity" &&
      threadProps.entityType !== "activity-session" &&
      threadProps.entityType !== "social" &&
      Boolean(
        threadProps.viewer?.id &&
          threadProps.post.author.id === threadProps.viewer.id,
      ));

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      focus.replace(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent
        ref={drawerRef}
        layer={COMMENT_DRAWER_LAYER}
        style={viewportStyle}
        className="mx-auto h-[calc(100dvh-env(safe-area-inset-top)-8px)] max-h-[calc(100dvh-env(safe-area-inset-top)-8px)] w-full max-w-lg rounded-t-[28px] border-border bg-background"
      >
        <DrawerHeader className="grid shrink-0 grid-cols-[2.5rem_1fr_2.5rem] items-center bg-background px-3 pt-2 pb-3 text-center">
          {focus.focusedCommentId != null ? (
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-full"
              onClick={focus.back}
              aria-label="Back"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          ) : (
            <span />
          )}
          <DrawerTitle className="border-b-0 text-base font-semibold text-text-primary">
            {focus.focusedCommentId == null ? "Comments" : "Thread"}
          </DrawerTitle>
          <span />
        </DrawerHeader>

        <CommentThread
          {...threadProps}
          active={open}
          canModerate={derivedCanModerate}
          focus={focus}
          rootClassName="min-h-0 flex-1"
          listClassName="pointer-events-auto no-scrollbar min-h-0 flex-1 overflow-y-auto pb-6"
          composerPortal={false}
          nestedDrawerLayer={COMMENT_NESTED_DRAWER_LAYER}
          composerClassName="relative z-10 w-full shrink-0 border-border border-t bg-background shadow-[0_-12px_28px_rgba(0,0,0,0.16)]"
        />
      </DrawerContent>
    </Drawer>
  );
}
